// Page: Analytics
// Purpose: Admin dashboard showing current and historical business analytics.
// Includes tabs, charts (Recharts), tooltips, and snapshot rendering.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import adminApi from '../../../api/accounts/adminApi';
import { isUserAdmin } from '../../../utils/admin/adminUtils';
import { 
  BarChart3,
  Calendar,
  RefreshCw,
  ArrowLeft,
  History
} from 'lucide-react';
import { AnalyticsSkeleton } from '../../../components/ui/feedback/skeleton.jsx';
import HistoricalAnalytics from '../../../components/admin/analytics/HistoricalAnalytics';
import RevenueMetrics from '../../../components/admin/analytics/RevenueMetrics';
import CustomerGrowthMetrics from '../../../components/admin/analytics/CustomerGrowthMetrics';
import TrialConversionMetrics from '../../../components/admin/analytics/TrialConversionMetrics';
import ExpensesMetrics from '../../../components/admin/analytics/ExpensesMetrics';
import ChurnRetentionMetrics from '../../../components/admin/analytics/ChurnRetentionMetrics';
import EngagementMetrics from '../../../components/admin/analytics/EngagementMetrics';
import PlatformActivityStats from '../../../components/admin/analytics/PlatformActivityStats';
import AnalyticsCharts from '../../../components/admin/analytics/AnalyticsCharts';
import CohortAnalysisTable from '../../../components/admin/analytics/CohortAnalysisTable';
import SignupTrendsChart from '../../../components/admin/analytics/SignupTrendsChart';
import LostUsersChart from '../../../components/admin/analytics/LostUsersChart';
import NetUsersGrowthChart from '../../../components/admin/analytics/NetUsersGrowthChart';
import RevenueCalculator from '../../../components/admin/analytics/RevenueCalculator';

const Analytics = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [activeTab, setActiveTab] = useState('current'); // 'current' or 'historical'
  const [users22, setUsers22] = useState(0);
  const [users34, setUsers34] = useState(0);
  const [annualUsers, setAnnualUsers] = useState(0);
  
  // Check if user is admin
  const isAdmin = isUserAdmin(user);

  const [analyticsData, setAnalyticsData] = useState({
    signupTrends: [],
    revenueTrends: [],
    customerTrends: [],
    conversionTrends: [],
    planDistribution: [],
    cohortAnalysis: [],
    lostUsersTrends: [],
    netUsersTrends: [],
    summary: {
      totalUsers: 0,
      payingCustomers: 0,
      trialUsers: 0,
      churnedUsers: 0,
      totalRevenue: 0,
      currentMRR: 0,
      averageRevenuePerUser: 0,
      averageLTV: 0,
      trialToPayConversionRate: 0,
      signupToPayConversionRate: 0,
      churnRate: 0,
      mrrGrowthRate: 0
    }
  });

  // Check admin access
  useEffect(() => {
    if (user) {
      if (!isAdmin) {
        navigate('/app');
        return;
      }
    } else if (user === null) {
      navigate('/login');
    }
  }, [user, isAdmin, navigate]);

  // Load analytics data
  const loadAnalyticsData = useCallback(async () => {
    if (!user || !isAdmin) {
      return;
    }

    try {
      setError(null);
      const data = await adminApi.getAnalytics(selectedPeriod);
      setAnalyticsData(data);
    } catch (err) {
      console.error('Analytics - Error loading data:', err);
      setError(err.message || 'Failed to load analytics data');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [user, isAdmin, selectedPeriod]);

  useEffect(() => {
    loadAnalyticsData();
  }, [loadAnalyticsData]);

  // Set default values when analytics data loads
  useEffect(() => {
    if (!analyticsData || !analyticsData.summary) return;
    if (users22 !== 0 || users34 !== 0 || annualUsers !== 0) return;

    const breakdown = analyticsData.summary.planBreakdown;
    if (breakdown) {
      setUsers22(breakdown.monthly22 || 0);
      setUsers34(breakdown.monthly34 || 0);
      setAnnualUsers(breakdown.annualMonthlyEquivalent || 0);
      return;
    }

    const currentPayingUsers = analyticsData.summary.payingCustomers || 0;
    const legacy22Users = Math.min(73, currentPayingUsers);
    const remainingUsers = Math.max(0, currentPayingUsers - legacy22Users);
    const monthly34Users = Math.floor(remainingUsers * 0.8);
    const annualUsersCount = remainingUsers - monthly34Users;
    setUsers22(legacy22Users);
    setUsers34(monthly34Users);
    setAnnualUsers(annualUsersCount);
  }, [analyticsData, users22, users34, annualUsers]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadAnalyticsData();
  };

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    setIsLoading(true);
  };

  // Utility functions passed to child components
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric'
    });
  };

  const formatDateLong = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getPeriodDisplayName = (period) => {
    switch (period) {
      case 'all': return 'All Time';
      case '1year': return 'Last Year';
      case '6months': return 'Last 6 Months';
      case '3months': return 'Last 3 Months';
      case '1month': return 'Last 30 Days';
      case '1week': return 'Last 7 Days';
      default: return 'All Time';
    }
  };

  const getDaysForPeriod = (period) => {
    switch (period) {
      case 'all': return 30;
      case '1year': return 365;
      case '6months': return 180;
      case '3months': return 90;
      case '1month': return 30;
      case '1week': return 7;
      default: return 30;
    }
  };

  const formatNumber = (value, fractionDigits = 1) => {
    const num = Number(value || 0);
    return new Intl.NumberFormat('en-US', {
      useGrouping: true,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(num);
  };

  const formatPercentage = (value) => {
    return `${formatNumber(value, 1)}%`;
  };

  // Chart colors matching your app theme
  const colors = {
    primary: '#3B82F6',
    secondary: '#10B981',
    accent: '#F59E0B',
    danger: '#EF4444',
    purple: '#8B5CF6',
    teal: '#14B8A6',
    rose: '#F43F5E',
    orange: '#F97316'
  };

  const pieColors = [colors.primary, colors.secondary, colors.accent, colors.purple];

  if (isLoading) {
    return <AnalyticsSkeleton />;
  }

  if (!isAdmin) {
    return null;
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6 w-full max-w-[1800px] mx-auto pb-20">
        <div className="bg-red-50/70 dark:bg-red-900/30 backdrop-blur-md border border-red-200 dark:border-red-800 rounded-lg p-4 sm:p-6">
          <h3 className="text-lg font-medium text-red-800 dark:text-red-400 mb-2">
            Error Loading Analytics
          </h3>
          <p className="text-red-600 dark:text-red-300">{error}</p>
          <button
            onClick={handleRefresh}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 w-full max-w-[1800px] mx-auto pb-20">
      {/* Back to Admin Users button */}
      <div className="mb-4">
        <button
          onClick={() => navigate('/app/private/admin/users')}
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Back to User Management
        </button>
      </div>

      {/* Page header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <BarChart3 className="h-6 w-6 text-primary-600 dark:text-primary-400 mr-2" />
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white">
            Business Analytics Dashboard
          </h1>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('current')}
            className={`inline-flex items-center px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
              activeTab === 'current'
                ? 'bg-primary-600 text-white shadow-sm'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Current Analytics
          </button>
          <button
            onClick={() => setActiveTab('historical')}
            className={`inline-flex items-center px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
              activeTab === 'historical'
                ? 'bg-primary-600 text-white shadow-sm'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <History className="w-4 h-4 mr-2" />
            Historical Trends
          </button>
        </div>
        
        {activeTab === 'current' && (
          <>
            <div className="text-gray-600 dark:text-gray-400 mb-4 text-sm sm:text-base space-y-2">
              <p>
                Comprehensive business metrics and insights for <strong>{getPeriodDisplayName(selectedPeriod)}</strong>.
              </p>
              <p>
                Free users are excluded from all calculations. <strong>Deleted paying customers</strong> and <strong>deleted trial users</strong> are included in historical analytics for complete trial conversion and churn analysis.
              </p>
              <p>
                Trial conversion rates are broken down by <strong>pricing era</strong> (pre vs. post price increase) for detailed performance analysis.
              </p>
            </div>

            {/* Controls */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-500 flex-shrink-0" />
                <select
                  value={selectedPeriod}
                  onChange={(e) => handlePeriodChange(e.target.value)}
                  className="flex-1 sm:flex-none px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-transparent backdrop-blur-md text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                >
                  <option value="all">All Time</option>
                  <option value="1year">Last Year</option>
                  <option value="6months">Last 6 Months</option>
                  <option value="3months">Last 3 Months</option>
                  <option value="1month">Last 30 Days</option>
                  <option value="1week">Last 7 Days</option>
                </select>
              </div>

              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-transparent backdrop-blur-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/30 focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm transition-colors"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </button>
            </div>
          </>
        )}

        {activeTab === 'historical' && (
          <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm sm:text-base">
            View historical business trends using daily snapshots. Track long-term growth patterns and make data-driven decisions based on your business history.
          </p>
        )}
      </div>

      {/* Content based on active tab */}
      {activeTab === 'current' && (
        <>
          <RevenueMetrics 
            analyticsData={analyticsData} 
            formatCurrency={formatCurrency} 
          />
          
          <CustomerGrowthMetrics 
            analyticsData={analyticsData} 
            formatCurrency={formatCurrency} 
            formatPercentage={formatPercentage}
          />
          
          <TrialConversionMetrics 
            analyticsData={analyticsData} 
            formatCurrency={formatCurrency} 
            formatPercentage={formatPercentage}
          />
          
          <ExpensesMetrics 
            analyticsData={analyticsData} 
            formatCurrency={formatCurrency}
          />
          
          <ChurnRetentionMetrics 
            analyticsData={analyticsData} 
            formatCurrency={formatCurrency}
          />
          
          <EngagementMetrics 
            analyticsData={analyticsData} 
            formatPercentage={formatPercentage}
            formatNumber={formatNumber}
          />
          
          <PlatformActivityStats 
            analyticsData={analyticsData} 
            formatNumber={formatNumber}
          />

          <AnalyticsCharts 
            analyticsData={analyticsData}
            formatDate={formatDate}
            formatDateLong={formatDateLong}
            formatCurrency={formatCurrency}
            formatNumber={formatNumber}
            getDaysForPeriod={getDaysForPeriod}
            selectedPeriod={selectedPeriod}
            colors={colors}
            pieColors={pieColors}
          />

          <div className="mt-8">
            <CohortAnalysisTable 
              analyticsData={analyticsData}
              formatPercentage={formatPercentage}
              formatCurrency={formatCurrency}
            />
          </div>

          <div className="mt-8">
            <SignupTrendsChart 
              analyticsData={analyticsData}
              getPeriodDisplayName={getPeriodDisplayName}
              selectedPeriod={selectedPeriod}
            />
          </div>

          <div className="mt-8">
            <LostUsersChart 
              analyticsData={analyticsData}
              formatDate={formatDate}
              formatDateLong={formatDateLong}
              getPeriodDisplayName={getPeriodDisplayName}
              selectedPeriod={selectedPeriod}
            />
          </div>

          <div className="mt-8">
            <NetUsersGrowthChart 
              analyticsData={analyticsData}
              formatDate={formatDate}
              formatDateLong={formatDateLong}
            />
          </div>

          <div className="mt-8">
            <RevenueCalculator 
              users22={users22}
              setUsers22={setUsers22}
              users34={users34}
              setUsers34={setUsers34}
              annualUsers={annualUsers}
              setAnnualUsers={setAnnualUsers}
              analyticsData={analyticsData}
              formatCurrency={formatCurrency}
            />
          </div>
        </>
      )}

      {/* Historical Analytics Tab */}
      {activeTab === 'historical' && (
        <HistoricalAnalytics />
      )}
    </div>
  );
};

export default Analytics;
